import * as Yup from "yup";
import {
  COMPANY_NATIONAL_ID_FORMAT_MSG,
  COMPANY_NATIONAL_ID_REQUIRED_MSG,
  validateCompanyNationalId,
} from "../../../infrastructure/utils/companyNationalIdRules";
import {
  NATIONAL_CODE_INVALID_MSG,
  validateNationalCode,
} from "../../../infrastructure/utils/nationalCodeRules";
import {
  normalizeIban,
  onlyDigits,
  validateBankAccount,
  validateCardNumber,
  validateIban,
} from "./contractorBankFields";

export const fa2enDigits = (value) =>
  String(value || "")
    .replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d))
    .replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d));

const digits = (value) => onlyDigits(fa2enDigits(value));

/**
 * Field validators aligned with public contractor registration form
 * (PersonalInfo + CompanyInfo + bank fields).
 */
export const buildContractorRegistrationDetailsSchema = () =>
  Yup.object().shape({
    status: Yup.number().required("لطفا وضعیت را انتخاب کنید."),
    registrationType: Yup.string().oneOf(["individual", "legal"]).required(),
    firstName: Yup.string()
      .trim()
      .required("نام الزامی است")
      .when("registrationType", {
        is: "legal",
        then: (schema) => schema.required("نام نماینده الزامی است"),
      }),
    lastName: Yup.string()
      .trim()
      .required("نام خانوادگی الزامی است")
      .when("registrationType", {
        is: "legal",
        then: (schema) => schema.required("نام خانوادگی نماینده الزامی است"),
      }),
    nationalCode: Yup.string()
      .required("کد ملی الزامی است")
      .test("national-len", "کد ملی باید 10 رقم باشد", (value) =>
        /^\d{10}$/.test(digits(value))
      )
      .test("national-code", NATIONAL_CODE_INVALID_MSG, (value) =>
        !validateNationalCode(digits(value))
      ),
    mobile: Yup.string()
      .required("شماره موبایل الزامی است")
      .test(
        "mobile",
        "شماره موبایل معتبر نیست (مثال: 09123456789)",
        (value) => /^09\d{9}$/.test(digits(value))
      ),
    email: Yup.string()
      .trim()
      .required("ایمیل الزامی است")
      .email("فرمت ایمیل معتبر نیست"),
    birthDate: Yup.mixed().required("تاریخ تولد الزامی است"),
    postalCode: Yup.string()
      .required("کد پستی الزامی است")
      .test("postal", "کد پستی باید 10 رقم باشد", (value) =>
        /^\d{10}$/.test(digits(value))
      ),
    workExperience: Yup.string().trim().required("سابقه کار الزامی است"),
    provinceId: Yup.mixed().test(
      "provinceId",
      "استان الزامی است",
      (value) => value !== null && value !== undefined && value !== ""
    ),
    province: Yup.string().trim().required("استان الزامی است"),
    cityId: Yup.mixed().test(
      "cityId",
      "شهرستان الزامی است",
      (value) => value !== null && value !== undefined && value !== ""
    ),
    city: Yup.string().trim().required("شهرستان الزامی است"),
    companyAddress: Yup.string()
      .trim()
      .when("registrationType", {
        is: "legal",
        then: (schema) => schema.required("آدرس شرکت الزامی است"),
        otherwise: (schema) => schema.required("آدرس الزامی است"),
      })
      .min(10, "آدرس باید حداقل 10 کاراکتر باشد")
      .max(200, "آدرس نباید بیش از 200 کاراکتر باشد"),
    ibanNumber: Yup.string()
      .required("شماره شبا الزامی است")
      .test("iban", function ibanTest(value) {
        const err = validateIban(value);
        return err ? this.createError({ message: err }) : true;
      }),
    bankAccountNumber: Yup.string()
      .required("شماره حساب الزامی است")
      .test("account", function accountTest(value) {
        const err = validateBankAccount(value);
        return err ? this.createError({ message: err }) : true;
      }),
    cardNumber: Yup.string()
      .required("شماره کارت الزامی است")
      .test("card", function cardTest(value) {
        const err = validateCardNumber(value);
        return err ? this.createError({ message: err }) : true;
      }),
    companyName: Yup.string().when("registrationType", {
      is: "legal",
      then: (schema) =>
        schema
          .trim()
          .required("نام شرکت الزامی است")
          .min(3, "نام شرکت باید حداقل 3 کاراکتر باشد"),
      otherwise: (schema) => schema.notRequired(),
    }),
    companyRegistrationNumber: Yup.string().when("registrationType", {
      is: "legal",
      then: (schema) =>
        schema
          .required("شماره ثبت شرکت الزامی است")
          .test(
            "company-reg",
            "شماره ثبت شرکت باید حداکثر 6 رقم عددی باشد",
            (value) => /^\d{1,6}$/.test(digits(value))
          ),
      otherwise: (schema) => schema.notRequired(),
    }),
    companyNationalId: Yup.string().when("registrationType", {
      is: "legal",
      then: (schema) =>
        schema
          .required(COMPANY_NATIONAL_ID_REQUIRED_MSG)
          .test("company-nid", COMPANY_NATIONAL_ID_FORMAT_MSG, (value) =>
            !validateCompanyNationalId(digits(value))
          ),
      otherwise: (schema) => schema.notRequired(),
    }),
    companyPhone: Yup.string().when("registrationType", {
      is: "legal",
      then: (schema) =>
        schema
          .required("تلفن شرکت الزامی است")
          .test(
            "company-phone",
            "تلفن شرکت باید 11 رقم باشد و با 0 شروع شود (مثال: 02112345678)",
            (value) => /^0\d{10}$/.test(digits(value))
          ),
      otherwise: (schema) => schema.notRequired(),
    }),
    foundedYear: Yup.number()
      .transform((value, originalValue) =>
        String(originalValue ?? "").trim() === "" ? null : value
      )
      .nullable()
      .when("registrationType", {
        is: "legal",
        then: (schema) =>
          schema
            .required("سال تاسیس الزامی است")
            .min(1200, "سال تاسیس معتبر نیست")
            .max(1600, "سال تاسیس معتبر نیست"),
        otherwise: (schema) => schema.nullable().notRequired(),
      }),
  });

export { normalizeIban, onlyDigits, digits as onlyFaDigits };
